import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiDelete, apiGet, apiPost } from '../../lib/api';

/**
 * Conexiunea la SPV a unei firme, dinspre client
 * ([#200](https://github.com/bplugaru/efx-pcrm/issues/200)).
 *
 * ⚠️ **Fără tokenuri, prin construcție**: serverul nu le trimite, iar schema de aici nu are unde
 * să le pună. Ecranul vede doar cine s-a conectat, când, și când expiră.
 */
const connectionSchema = z.object({
  /** `connected` · `reconnect` (refuzată de ANAF sau cu tokenul de reînnoire expirat). */
  state: z.enum(['connected', 'reconnect']),
  connectedBy: z.string(),
  connectedAt: z.string(),
  accessExpiresAt: z.string(),
  refreshExpiresAt: z.string(),
  refreshedAt: z.string().nullable(),
  lastError: z.string().nullable(),
  lastErrorAt: z.string().nullable(),
});

const statusSchema = z.object({
  /** `false` = serverul n-are `SPV_ENCRYPTION_KEY`: SPV-ul e oprit acolo. */
  enabled: z.boolean(),
  /** `false` = lipsesc Client ID-ul sau Client Secret-ul din Setări → ANAF (SPV). */
  configured: z.boolean(),
  /** Unde vor pleca facturile: mediul de test e-Factura sau cel real. */
  environment: z.enum(['test', 'prod', 'other']).nullable(),
  eFacturaUrl: z.string().nullable(),
  /** Callback URL-ul pe care îl trimite serverul: trebuie să fie exact cel înregistrat la ANAF. */
  redirectUri: z.string().nullable(),
  connection: connectionSchema.nullable(),
});

export type SpvConnection = z.infer<typeof connectionSchema>;
export type SpvStatus = z.infer<typeof statusSchema>;

export const spvQueryKey = (companyId: string) => ['companies', companyId, 'spv'] as const;

export const spvQueryOptions = (companyId: string) =>
  queryOptions({
    queryKey: spvQueryKey(companyId),
    queryFn: (): Promise<SpvStatus> => apiGet(`/companies/${companyId}/spv`, statusSchema),
  });

/** Adresa `logincert` la care pleacă browserul ca omul să-și aleagă certificatul. */
export function startSpvConnection(companyId: string): Promise<string> {
  return apiPost(`/companies/${companyId}/spv/connect`, {}, z.object({ url: z.string() })).then(
    (body) => body.url,
  );
}

export function renewSpvNow(companyId: string) {
  return apiPost(
    `/companies/${companyId}/spv/renew`,
    {},
    z.object({
      result: z.enum(['renewed', 'refused', 'unreachable', 'missing']),
      message: z.string().nullable(),
    }),
  );
}

export function disconnectSpv(companyId: string): Promise<void> {
  return apiDelete(`/companies/${companyId}/spv`);
}
